import type { CalendarMoonDayResponse } from '@/api/schema/calendar'
import { AiQuestions } from '@/app/components/AiQuestions/AiQuestions'
import { CalendarCard } from '@/app/components/CalendarCard/CalendarCard'
import { EventsCard } from '@/app/components/EventsCard/EventsCard'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { MenuFooter } from '@/app/components/MenuFooter/MenuFooter'
import { MoonState } from '@/app/components/MoonState/MoonState'
import { Motion } from '@/app/components/Motion/Motion'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import DoSvg from '@/app/icons/do.svg?react'
import { api } from '@/app/utils/api'
import { useCalendarSSRData } from '@/app/utils/app-data'
import { formatPeriodDates } from '@/app/utils/date'
import { getMoonQuestionContext, getMoonReferenceAt } from '@/app/utils/moon'
import type { FunctionalComponent } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation, useParams, useSearch } from 'wouter-preact'
import styles from './index.module.scss'

export const MoonDayPage: FunctionalComponent = () => {
  const back = useBack('~/tools')
  const params = useParams<{ num: string }>()
  const search = useSearch()
  const [pathname] = useLocation()
  const at = getMoonReferenceAt(search)
  const initialData = useCalendarSSRData(`${pathname}${search}`)
  const initialResponse = initialData?.type === 'moon-day' ? initialData.response : null
  const [resp, setResp] = useState<CalendarMoonDayResponse | null>(initialResponse)
  const skipInitialRequest = useRef(Boolean(initialResponse))

  useEffect(() => {
    if (skipInitialRequest.current) {
      skipInitialRequest.current = false
      return
    }

    setResp(null)
    api.get<CalendarMoonDayResponse>(`/calendar/moon/day/${params.num}`, {
      params: { at },
    })
      .then(r => setResp(r.data))
  }, [params.num, at])

  const periodDates = resp
    ? formatPeriodDates(resp.period.startAt, resp.period.endAt)
    : undefined

  function makeContext() {
    if (!resp) return undefined

    return getMoonQuestionContext(at, resp.period.startAt, resp.title)
  }

  return (
    <Layout>
      <Layout.Content
        onBack={back}
      >
        {!resp
          ? (
            <Loader.Container>
              <Loader type='base' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Layout.Header>
                Лунный календарь
              </Layout.Header>

              <Row gap={15}>
                <CalendarCard
                  as='h1'
                  date={periodDates}
                  title={resp.title}
                  subTitle={
                    <MoonState
                      at={resp.period.phase.at}
                      phaseId={resp.period.phase.id}
                      phaseName={resp.period.phase.title}
                      sing={resp.moonSign}
                      location={resp.location}
                    />
                  }
                  text={resp.summary}
                />

                <UI.Panel>
                  <Row gap={10}>
                    <Col gap={10}>
                      <UI.Avatar bot='zoe' size={40} />
                      <Row>
                        <Text
                          fontSize={18}
                          fontWeight={600}
                        >
                          Зоя
                        </Text>
                        <Text
                          fontSize={13}
                          opacity={0.5}
                        >
                          AI-астрлог
                        </Text>
                      </Row>
                    </Col>

                    <Row gap={15}>
                      {resp.text.split('\n\n').map(paragraph => (
                        <Text
                          key={paragraph}
                          as='p'
                          fontSize={16}
                        >
                          {paragraph}
                        </Text>
                      ))}
                    </Row>
                  </Row>
                </UI.Panel>

                <UI.Panel>
                  <Row gap={10}>
                    <Text
                      upper
                      as='h2'
                      fontSize={13}
                      fontWeight={600}
                    >
                      Что делать
                    </Text>
                    <Row
                      as='ul'
                      gap={6}
                      class={styles.list}
                    >
                      {resp.do.map(item => (
                        <Col
                          as='li'
                          gap={5}
                          alignItems='center'
                        >
                          <DoSvg width={19} height={19} class={styles.do} />
                          <Text fontSize={16}>{item}</Text>
                        </Col>
                      ))}
                    </Row>
                  </Row>
                </UI.Panel>

                <EventsCard
                  events={resp.upcomingEvents}
                  from={resp.period.startAt}
                />

                <AiQuestions
                  context={makeContext()}
                  questions={resp.questions}
                />
              </Row>
            </Motion>
          )}

        <MenuFooter />
      </Layout.Content>
    </Layout>
  )
}
